import React, { useEffect, useState } from 'react';
import {
	LoadingOverlay,
	Button,
	Title,
	Text,
	PasswordInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getErrorMessage, ErrorCode } from '@/types/ErrorCodes';
import { validatePassword } from '@/utils/utilities';
import { AppPaths } from '@/configs/appPaths';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import { useMutation } from '@tanstack/react-query';
import logo from '@/assets/images/logo.png';
import { SubmitStatus } from './Login';
import { UncontrolledAlert } from 'reactstrap';

const initialValues = {
	password: '',
	repeatPassword: '',
};

export const SetNewPassword: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');
	const [status, setStatus] = useState(SubmitStatus.THROTTLE);

	useEffect(() => {
		if (!token) {
			navigate(`/${AppPaths.LOGIN}`);
		}
	}, [navigate, token]);

	const form = useForm({
		initialValues,
		validate: {
			password: (value: string, values: typeof initialValues) => {
				if (value !== values.repeatPassword)
					return 'Oba hasła muszą być takie same';
				if (!validatePassword(value))
					return getErrorMessage([ErrorCode.INVALID_PASSWORD]);
				return null;
			},
			repeatPassword: (value: string, values: typeof initialValues) => {
				if (value !== values.password) return 'Oba hasła muszą być takie same';
				if (!validatePassword(value))
					return getErrorMessage([ErrorCode.INVALID_PASSWORD]);
				return null;
			},
		},
	});

	const newPasswordMutation = useMutation({
		mutationFn: UnauthenticatedApi.resetPassword,
		onMutate: () => {
			setStatus(SubmitStatus.LOADING);
		},
		onSuccess: () => {
			setStatus(SubmitStatus.SUCCESS);
			form.reset();
			setTimeout(() => {
				navigate(`/${AppPaths.LOGIN}`);
			}, 3000);
		},
		onError: (error) => {
			setStatus(SubmitStatus.ERROR);
		},
	});

	const handleSubmit = form.onSubmit((values) => {
		if (!token) return;
		newPasswordMutation.mutate({
			hash: token,
			password: values.password,
		});
	});

	return (
		<div className="account-pages">
			<div className="container">
				<div className="row justify-content-center">
					<div className="col-md-8 col-lg-6 col-xl-5">
						<div className="card overflow-hidden">
							<LoadingOverlay visible={status === SubmitStatus.LOADING} />
							<div className="logo-bar">
								<img alt="logo" src={logo} />
							</div>
							<div className="card-body p-4">
								<Title order={2} mb="md">
									Utwórz nowe hasło
								</Title>

								<Text size="sm" c="dimmed" mb="lg">
									Hasło musi zawierać:
									<ul className="mt-2 mb-3">
										<li>Minimum 6 znaków</li>
										<li>Przynajmniej jedną wielką literę</li>
										<li>Przynajmniej jedną cyfrę</li>
										<li>Przynajmniej jeden znak specjalny</li>
									</ul>
								</Text>

								{status === SubmitStatus.SUCCESS && (
									<UncontrolledAlert color="success" className="mb-3">
										Hasło zostało zmienione pomyślnie! Za chwilę nastąpi
										przekierowanie do strony logowania.
									</UncontrolledAlert>
								)}

								{status === SubmitStatus.ERROR && (
									<UncontrolledAlert color="danger" className="mb-3">
										Nie udało się zresetować hasła. Spróbuj ponownie lub poproś
										o nowy link resetujący.
									</UncontrolledAlert>
								)}

								<form onSubmit={handleSubmit}>
									<PasswordInput
										label="Hasło"
										placeholder="Wpisz hasło"
										required
										mb="md"
										{...form.getInputProps('password')}
										disabled={status === SubmitStatus.SUCCESS}
									/>

									<PasswordInput
										label="Powtórz hasło"
										placeholder="Wpisz hasło ponownie"
										required
										mb="lg"
										{...form.getInputProps('repeatPassword')}
										disabled={status === SubmitStatus.SUCCESS}
									/>

									<div className="d-flex justify-content-between">
										<Button
											variant="outline"
											onClick={() => navigate(`/${AppPaths.LOGIN}`)}
										>
											Wróć
										</Button>
										{status !== SubmitStatus.SUCCESS && (
											<Button type="submit">Resetuj hasło</Button>
										)}
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
